import React, { FC, useState } from 'react';
import { Form, Input, message, Modal, Select } from 'antd';
import { useMutation } from '@apollo/client';
import { RespondToReferral } from '../../../../GraphQL';
import { ReferralData, QUERY } from './GetReferralQuery';
import { LIST_AUDIT_LOG_QUERY } from './ListAuditLogQuery';
import { useAppContext } from '../../../../hooks';
import { deletePaginatedCacheItems } from '../../../../helpers/cache';
import { useBlockitTheme } from '../../../../themes/useTheme';
import { DEFAULT_REJECTION_REASONS } from './rejectionReasons';

interface Props {
  referral: ReferralData;
  organizationId: string;
  visible: boolean;
  onClose: () => void;
}

export const DeclineReferralModal: FC<Props> = ({ referral, organizationId, visible, onClose }): JSX.Element => {
  const { currentOrganization } = useAppContext();
  const { isAlluvium, branding } = useBlockitTheme();

  const orgReasons = currentOrganization?.referralRejectionReasons;
  const reasons = orgReasons?.length ? orgReasons : DEFAULT_REJECTION_REASONS;

  const defaultReason = reasons[0].value || reasons[0].key;

  const [reason, setReason] = useState<string>(defaultReason);
  const [reasonText, setReasonText] = useState<string | null>(null);

  const [respondToReferral, { loading }] = useMutation(RespondToReferral);

  const onOk = (): void => {
    const reasonLabel = reasons.find(r => (r.value || r.key) === reason)?.key || reason;

    respondToReferral({
      variables: {
        referralId: referral.id,
        action: 'DECLINED',
        reason: reasonText || reasonLabel,
      },
      refetchQueries: [
        { query: QUERY, variables: { id: referral.id, organizationId } },
        {
          query: LIST_AUDIT_LOG_QUERY,
          variables: {
            action: 'referral.receiver_response',
            recordId: referral.id,
            organizationId,
          },
        },
      ],
      update: (cache: any) => {
        message.success('Receiver response: Declined');
        deletePaginatedCacheItems(cache, 'ListReferrals');
        onClose();
      },
      onError: () => {
        message.error('Failed to decline referral');
      },
    });
  };

  return (
    <Modal
      title="Receiver Response"
      visible={visible}
      onOk={onOk}
      onCancel={onClose}
      confirmLoading={loading}
      okButtonProps={
        isAlluvium ? { style: { backgroundColor: branding.primaryColor, borderColor: branding.primaryColor } } : {}
      }
    >
      <Form>
        <Form.Item label="Reason for declining">
          <Select<string>
            placeholder="Choose reason"
            defaultValue={defaultReason}
            size="small"
            onChange={val => setReason(val)}
          >
            {reasons.map(r => (
              <Select.Option key={r.key} value={r.value || r.key}>
                {r.key}
              </Select.Option>
            ))}
          </Select>
          {reason === 'other' && (
            <Input
              maxLength={128}
              onChange={e => setReasonText(e.target.value)}
              placeholder="Additional detail"
              size="small"
            />
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};
